import { create } from 'zustand'
import { toast } from 'sonner'
import MessagesService from '@/services/messages.service'
import type { Conversation, Message } from '@/types'
import { STORAGE_KEYS } from '@/utils/app-constants'
import { getConversationIdentityKey, normalizeComparableJid } from '@/utils/contactIdentity'

export interface ActiveChatSnapshot {
  instance_id: string
  conversation_id: string
  contact_id: string
  last_message_id?: string | null
}

function isTransientError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as { response?: { status?: number } }
  const status = e.response?.status
  // 429 = rate limit, undefined response = network/CORS error — both are transient
  return status === 429 || status === undefined
}

function getMessageIdentity(message: Message): string {
  if (message.provider_message_id) return `provider:${message.provider_message_id}`
  if (message.message_id_wa) return `wa:${message.message_id_wa}`
  return `id:${message.id}`
}

function getMessageSortTime(message: Message): number {
  return new Date(message.provider_created_at ?? message.created_at).getTime()
}

function buildMessagePreview(message: Message): string | undefined {
  if (message.content?.trim()) return message.content

  switch (message.message_type) {
    case 'audio': {
      if (message.media_duration_seconds && message.media_duration_seconds > 0) {
        const minutes = Math.floor(message.media_duration_seconds / 60)
        const seconds = message.media_duration_seconds % 60
        return `Audio (${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')})`
      }
      return 'Audio'
    }
    case 'image':
      return 'Imagem'
    case 'video':
      return 'Video'
    case 'document':
      return 'Documento'
    case 'sticker':
      return 'Sticker'
    default:
      return undefined
  }
}

function mergeMessages(messages: Message[]): Message[] {
  const grouped = new Map<string, Message>()

  for (const message of messages) {
    const identity = getMessageIdentity(message)
    const current = grouped.get(identity)
    grouped.set(identity, current ? { ...current, ...message } : message)
  }

  return Array.from(grouped.values()).sort((left, right) => {
    const primaryDelta = getMessageSortTime(left) - getMessageSortTime(right)
    if (primaryDelta !== 0) return primaryDelta
    return new Date(left.created_at).getTime() - new Date(right.created_at).getTime()
  })
}

function pickLatestConversation(left: Conversation, right: Conversation): Conversation {
  const leftTime = left.last_message_at ? new Date(left.last_message_at).getTime() : 0
  const rightTime = right.last_message_at ? new Date(right.last_message_at).getTime() : 0
  if (rightTime > leftTime) return right
  if (leftTime > rightTime) return left
  return new Date(right.created_at).getTime() > new Date(left.created_at).getTime() ? right : left
}

function mergeConversation(current: Conversation, incoming: Conversation): Conversation {
  const preferred = pickLatestConversation(current, incoming)
  return {
    ...preferred,
    contact: incoming.contact ?? current.contact,
    unread_count: Math.max(current.unread_count ?? 0, incoming.unread_count ?? 0),
    last_message_preview: incoming.last_message_preview ?? current.last_message_preview,
    last_message_direction: incoming.last_message_direction ?? current.last_message_direction,
  }
}

function dedupeConversations(conversations: Conversation[]): Conversation[] {
  const grouped = new Map<string, Conversation>()

  for (const conversation of conversations) {
    const key = getConversationIdentityKey(conversation) || normalizeComparableJid(conversation.contact?.jid) || conversation.id
    const current = grouped.get(key)
    if (!current) {
      grouped.set(key, conversation)
      continue
    }

    const preferred = pickLatestConversation(current, conversation)
    const messageDates = [current.last_message_at, conversation.last_message_at]
      .filter(Boolean)
      .sort()
    const mergedLastMessageAt = messageDates[messageDates.length - 1]
    grouped.set(key, {
      ...preferred,
      unread_count: (current.unread_count ?? 0) + (conversation.unread_count ?? 0),
      last_message_at: mergedLastMessageAt ?? preferred.last_message_at,
    })
  }

  return [...grouped.values()].sort((left, right) => {
    const leftTime = left.last_message_at ? new Date(left.last_message_at).getTime() : 0
    const rightTime = right.last_message_at ? new Date(right.last_message_at).getTime() : 0
    return rightTime - leftTime
  })
}

function readActiveChatSnapshot(): ActiveChatSnapshot | null {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.ACTIVE_CHAT)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ActiveChatSnapshot>
    if (!parsed.instance_id || !parsed.conversation_id || !parsed.contact_id) {
      return null
    }
    return {
      instance_id: parsed.instance_id,
      conversation_id: parsed.conversation_id,
      contact_id: parsed.contact_id,
      last_message_id: parsed.last_message_id ?? null,
    }
  } catch {
    return null
  }
}

function writeActiveChatSnapshot(snapshot: ActiveChatSnapshot | null): void {
  if (typeof window === 'undefined') return

  try {
    if (!snapshot) {
      window.localStorage.removeItem(STORAGE_KEYS.ACTIVE_CHAT)
      return
    }

    window.localStorage.setItem(STORAGE_KEYS.ACTIVE_CHAT, JSON.stringify(snapshot))
  } catch {
    // Local persistence is best-effort only.
  }
}

export function getStoredActiveChat(): ActiveChatSnapshot | null {
  return readActiveChatSnapshot()
}

export function setStoredActiveChat(snapshot: ActiveChatSnapshot | null): void {
  writeActiveChatSnapshot(snapshot)
}

export function clearStoredActiveChat(): void {
  writeActiveChatSnapshot(null)
}

interface MessagesStore {
  conversations: Conversation[]
  activeConversation: Conversation | null
  messages: Message[]
  isLoadingConversations: boolean
  isLoadingMessages: boolean
  typingJids: Set<string>
  fetchConversations: (
    instanceId: string,
    params?: { status?: string; search?: string },
  ) => Promise<void>
  fetchWorkspaceConversations: (
    params?: { status?: string; search?: string },
  ) => Promise<void>
  fetchMessages: (instanceId: string, contactId: string) => Promise<void>
  replaceMessages: (messages: Message[]) => void
  addMessage: (message: Message) => void
  upsertConversation: (conversation: Conversation) => void
  updateMessageStatus: (
    messageId: string,
    status: Message['status'],
  ) => void
  markMessageRendered: (
    messageId: string,
    clientReceivedAt: string,
    clientRenderedAt: string,
  ) => void
  setActiveConversation: (conversation: Conversation) => void
  clearActiveConversation: (options?: { preserveStorage?: boolean }) => void
  updateConversationUnread: (conversationId: string, count: number) => void
  markConversationRead: (contactId: string, conversationId?: string | null) => void
  removeConversation: (conversationId: string) => void
  setTyping: (jid: string, isTyping: boolean) => void
}

export const useMessagesStore = create<MessagesStore>((set) => ({
  conversations: [],
  activeConversation: null,
  messages: [],
  isLoadingConversations: false,
  isLoadingMessages: false,
  typingJids: new Set(),

  fetchConversations: async (instanceId: string, params = {}) => {
    set({ isLoadingConversations: true })
    try {
      const result = await MessagesService.getConversations(instanceId, params)
      set({ conversations: dedupeConversations(result.data) })
    } catch (err) {
      if (!isTransientError(err)) toast.error('Erro ao carregar conversas.')
    } finally {
      set({ isLoadingConversations: false })
    }
  },

  fetchWorkspaceConversations: async (params = {}) => {
    set({ isLoadingConversations: true })
    try {
      const result = await MessagesService.getWorkspaceConversations({
        ...params,
        per_page: 100,
      })
      set({ conversations: dedupeConversations(result.data) })
    } catch (err) {
      if (!isTransientError(err)) toast.error('Erro ao carregar conversas.')
    } finally {
      set({ isLoadingConversations: false })
    }
  },

  fetchMessages: async (instanceId: string, contactId: string) => {
    set({ isLoadingMessages: true })
    try {
      const result = await MessagesService.getMessages(instanceId, contactId)
      const sorted = mergeMessages(result.data)
      const lastMsg = sorted[sorted.length - 1]
      set((state) => {
        const targetConversation =
          state.activeConversation?.contact_id === contactId
            ? state.activeConversation
            : state.conversations.find((conversation) => conversation.contact_id === contactId) ??
              null

        if (targetConversation) {
          writeActiveChatSnapshot({
            instance_id: instanceId,
            conversation_id: targetConversation.id,
            contact_id: targetConversation.contact_id,
            last_message_id: lastMsg?.id ?? null,
          })
        }

        return {
          messages: sorted,
          conversations: state.conversations.map((conversation) =>
            conversation.contact_id === contactId
              ? {
                  ...conversation,
                  unread_count: 0,
                  ...(lastMsg
                        ? {
                        last_message_preview: buildMessagePreview(lastMsg),
                        last_message_direction: lastMsg.direction,
                      }
                    : {}),
                }
              : conversation,
          ),
        }
      })
    } catch (err) {
      if (!isTransientError(err)) toast.error('Erro ao carregar mensagens.')
    } finally {
      set({ isLoadingMessages: false })
    }
  },

  replaceMessages: (incomingMessages: Message[]) => {
    set((state) => ({
      messages: mergeMessages(incomingMessages),
      conversations: dedupeConversations(
        state.conversations.map((conversation) => {
          const relatedMessages = mergeMessages(
            incomingMessages.filter((message) => message.conversation_id === conversation.id),
          )
          const latestMessage = relatedMessages[relatedMessages.length - 1]

          if (!latestMessage) return conversation

          return {
            ...conversation,
            last_message_at: latestMessage.provider_created_at ?? latestMessage.created_at,
            last_message_preview: buildMessagePreview(latestMessage) ?? conversation.last_message_preview,
            last_message_direction: latestMessage.direction,
          }
        }),
      ),
    }))
  },

  addMessage: (message: Message) => {
    set((state) => {
      const { messages, conversations, activeConversation } = state

      // Determine if this message belongs to the active conversation
      const belongsToActive =
        activeConversation !== null &&
        activeConversation !== undefined &&
        (
          // Prefer exact conversation_id match when available
          (message.conversation_id
            ? message.conversation_id === activeConversation.id
            : false) ||
          (message.contact_id ? message.contact_id === activeConversation.contact_id : false) ||
          // Fall back to JID matching against the active contact
          (normalizeComparableJid(activeConversation.contact?.jid) === normalizeComparableJid(message.from_jid) ||
            normalizeComparableJid(activeConversation.contact?.jid) === normalizeComparableJid(message.to_jid))
        )

      // Deduplicate: only check messages array if the message belongs here
      const incomingIdentity = getMessageIdentity(message)

      if (belongsToActive && activeConversation) {
        writeActiveChatSnapshot({
          instance_id: activeConversation.instance_id,
          conversation_id: activeConversation.id,
          contact_id: activeConversation.contact_id,
          last_message_id: message.id,
        })
      }

      // Find the matching conversation in the list (using conversation_id or JID)
      const convIndex = conversations.findIndex(
        (c) =>
          (message.conversation_id && c.id === message.conversation_id) ||
          (message.contact_id && c.contact_id === message.contact_id) ||
          normalizeComparableJid(c.contact?.jid) === normalizeComparableJid(message.from_jid) ||
          normalizeComparableJid(c.contact?.jid) === normalizeComparableJid(message.to_jid) ||
          c.contact?.jid === message.from_jid ||
          c.contact?.jid === message.to_jid,
      )

      // Build updated conversations list (metadata + reorder) in a single pass
      let updatedConversations = conversations
      if (convIndex !== -1) {
        const isActive = activeConversation?.id === conversations[convIndex].id
        const updatedConv = {
          ...conversations[convIndex],
          last_message_at: message.provider_created_at ?? message.created_at,
          unread_count: isActive
            ? 0
            : (conversations[convIndex].unread_count ?? 0) +
              (message.direction === 'inbound' ? 1 : 0),
          last_message_preview: buildMessagePreview(message) ?? conversations[convIndex].last_message_preview,
          last_message_direction: message.direction,
        }
        updatedConversations = [...conversations]
        updatedConversations[convIndex] = updatedConv
        // Move to top if not already there
        if (convIndex > 0) {
          updatedConversations.splice(convIndex, 1)
          updatedConversations.unshift(updatedConv)
        }
      }

      return {
        // Only append to messages if it belongs to the active conversation
        ...(belongsToActive ? { messages: mergeMessages([...messages, message]) } : {}),
        conversations: dedupeConversations(updatedConversations),
      }
    })
  },

  upsertConversation: (conversation: Conversation) => {
    set((state) => {
      const incomingKey = getConversationIdentityKey(conversation)

      // Conversa fechada: some da lista (e do ativo, se for o caso).
      // So reaparece quando chegar nova mensagem inbound (addMessage a recria).
      if (conversation.status === 'closed') {
        if (readActiveChatSnapshot()?.conversation_id === conversation.id) {
          clearStoredActiveChat()
        }
        return {
          conversations: state.conversations.filter((item) => item.id !== conversation.id),
          activeConversation:
            state.activeConversation?.id === conversation.id ? null : state.activeConversation,
          messages:
            state.activeConversation?.id === conversation.id ? [] : state.messages,
        }
      }

      const existing = state.conversations.find((item) => {
        if (item.id === conversation.id) return true
        return incomingKey !== '' && getConversationIdentityKey(item) === incomingKey
      })
      const conversations = existing
        ? state.conversations.map((item) =>
            item.id === existing.id ? mergeConversation(item, conversation) : item,
          )
        : [conversation, ...state.conversations]

      const nextActiveConversation =
        state.activeConversation && (
          state.activeConversation.id === conversation.id ||
          (incomingKey !== '' && getConversationIdentityKey(state.activeConversation) === incomingKey)
        )
          ? mergeConversation(state.activeConversation, conversation)
          : state.activeConversation

      if (nextActiveConversation) {
        const currentSnapshot = readActiveChatSnapshot()
        writeActiveChatSnapshot({
          instance_id: nextActiveConversation.instance_id,
          conversation_id: nextActiveConversation.id,
          contact_id: nextActiveConversation.contact_id,
          last_message_id:
            currentSnapshot?.conversation_id === nextActiveConversation.id
              ? currentSnapshot.last_message_id ?? null
              : null,
        })
      }

      return {
        conversations: dedupeConversations(conversations),
        activeConversation: nextActiveConversation,
      }
    })
  },

  updateMessageStatus: (messageId: string, status: Message['status']) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === messageId ? { ...msg, status } : msg,
      ),
    }))
  },

  markMessageRendered: (messageId: string, clientReceivedAt: string, clientRenderedAt: string) => {
    set((state) => ({
      messages: state.messages.map((message) =>
        message.id === messageId
          ? {
              ...message,
              sync_status: 'rendered',
              client_received_at: clientReceivedAt,
              client_rendered_at: clientRenderedAt,
              last_sync_check_at: clientRenderedAt,
            }
          : message,
      ),
    }))
  },

  setActiveConversation: (conversation: Conversation) => {
    set((state) => {
      const existingSnapshot = readActiveChatSnapshot()
      const lastMessageId =
        state.activeConversation?.id === conversation.id
          ? state.messages[state.messages.length - 1]?.id ?? null
          : existingSnapshot?.conversation_id === conversation.id
            ? existingSnapshot.last_message_id ?? null
            : null

      writeActiveChatSnapshot({
        instance_id: conversation.instance_id,
        conversation_id: conversation.id,
        contact_id: conversation.contact_id,
        last_message_id: lastMessageId,
      })

      return {
        activeConversation: conversation,
        messages: [],
        conversations: state.conversations.map((conv) =>
          conv.id === conversation.id || conv.contact_id === conversation.contact_id
            ? { ...conv, unread_count: 0 }
            : conv,
        ),
      }
    })
  },

  clearActiveConversation: (options) => {
    if (!options?.preserveStorage) {
      clearStoredActiveChat()
    }
    set({
      activeConversation: null,
      messages: [],
    })
  },

  updateConversationUnread: (conversationId: string, count: number) => {
    set((state) => ({
      conversations: dedupeConversations(state.conversations.map((conv) =>
        conv.id === conversationId ? { ...conv, unread_count: count } : conv,
      )),
    }))
  },

  markConversationRead: (contactId: string, conversationId?: string | null) => {
    set((state) => ({
      conversations: dedupeConversations(
        state.conversations.map((conv) =>
          conv.contact_id === contactId || (conversationId ? conv.id === conversationId : false)
            ? { ...conv, unread_count: 0 }
            : conv,
        ),
      ),
      activeConversation:
        state.activeConversation &&
        (state.activeConversation.contact_id === contactId ||
          (conversationId ? state.activeConversation.id === conversationId : false))
          ? { ...state.activeConversation, unread_count: 0 }
          : state.activeConversation,
    }))
  },

  removeConversation: (conversationId: string) => {
    if (readActiveChatSnapshot()?.conversation_id === conversationId) {
      clearStoredActiveChat()
    }
    set((state) => ({
      conversations: state.conversations.filter((item) => item.id !== conversationId),
      activeConversation:
        state.activeConversation?.id === conversationId ? null : state.activeConversation,
      messages: state.activeConversation?.id === conversationId ? [] : state.messages,
    }))
  },

  setTyping: (jid: string, isTyping: boolean) => {
    set((state) => {
      const newSet = new Set(state.typingJids)
      if (isTyping) {
        newSet.add(jid)
      } else {
        newSet.delete(jid)
      }
      return { typingJids: newSet }
    })
  },
}))
